import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ShellLanguagePairEntitlement,
  ShellProfileResponse,
  ShellSubscriptionStatus,
  ShellSubscriptionTier,
  ShellUser,
} from '@/shared/shell-api-types';

type User = ShellUser;

interface AuthState {
  user: User | null;
  sessionStatus: 'unknown' | 'authenticated' | 'unauthenticated';
  isCheckingSession: boolean;
  sessionError: string | null;
  /** Set when the auth store sees a tier transition FREE → PREMIUM/PROFESSIONAL
   * after a Stripe webhook lands. Consumed (and cleared) by the
   * <TierUpgradeToast> component. */
  pendingUpgradeToast: ShellSubscriptionTier | null;
  setAuth: (user: User) => void;
  setOnboardingComplete: (complete: boolean) => void;
  clearAuth: () => void;
  checkSession: () => Promise<void>;
  clearSessionError: () => void;
  dismissUpgradeToast: () => void;
  logout: () => void;
}

/** Compute the upgrade-toast transition: returns the new paid tier only when
 * the user upgraded from FREE (or had no record yet) on this session refresh.
 * Side-grades (PREMIUM → PREMIUM) and downgrades (PREMIUM → FREE) return
 * null so we never toast inappropriately. */
function detectUpgrade(previous: User | null, next: User): ShellSubscriptionTier | null {
  const prevTier = previous?.subscriptionTier ?? 'FREE';
  const nextTier = next.subscriptionTier ?? 'FREE';
  if (prevTier === 'FREE' && (nextTier === 'PREMIUM' || nextTier === 'PROFESSIONAL')) {
    return nextTier;
  }
  return null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      sessionStatus: 'unknown',
      isCheckingSession: false,
      sessionError: null,
      pendingUpgradeToast: null,
      setAuth: (user) =>
        set((state) => ({
          user,
          sessionStatus: 'authenticated',
          pendingUpgradeToast: detectUpgrade(state.user, user) ?? state.pendingUpgradeToast,
        })),
      dismissUpgradeToast: () => set({ pendingUpgradeToast: null }),
      setOnboardingComplete: (complete) =>
        set((state) =>
          state.user ? { user: { ...state.user, onboardingComplete: complete } } : state
        ),
      clearAuth: () => set({ user: null, sessionStatus: 'unauthenticated' }),
      clearSessionError: () => set({ sessionError: null }),
      checkSession: async () => {
        if (get().isCheckingSession) return;
        set({ isCheckingSession: true });

        try {
          const response = await fetch('/api/protected/profile', {
            method: 'GET',
            credentials: 'include',
          });

          if (!response.ok) {
            set({
              user: null,
              sessionStatus: 'unauthenticated',
              isCheckingSession: false,
              sessionError:
                response.status >= 500 ? 'Session check failed. Please try again.' : null,
            });
            return;
          }

          const payload = (await response.json()) as ShellProfileResponse;
          const user = payload?.user;
          if (!user?.id || !user?.email) {
            set({
              user: null,
              sessionStatus: 'unauthenticated',
              isCheckingSession: false,
              sessionError: null,
            });
            return;
          }

          const nextUser: User = {
            ...user,
            adminSummary: user.adminSummary ?? payload.adminSummary ?? null,
          };
          set((state) => ({
            user: nextUser,
            sessionStatus: 'authenticated',
            isCheckingSession: false,
            sessionError: null,
            pendingUpgradeToast: detectUpgrade(state.user, nextUser) ?? state.pendingUpgradeToast,
          }));
        } catch {
          set({
            user: null,
            sessionStatus: 'unauthenticated',
            isCheckingSession: false,
            sessionError:
              'Session check failed. You can continue, but you may need to sign in again.',
          });
        }
      },
      logout: () => {
        void fetch('/api/auth/signout', { method: 'POST', credentials: 'include' }).catch(() => {});
        set({ user: null, sessionStatus: 'unauthenticated' });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
);

// Selector hooks for Phase 2 entitlement-aware UI.
export const useSubscriptionTier = (): ShellSubscriptionTier =>
  useAuthStore((state) => state.user?.subscriptionTier ?? 'FREE');

export const useSubscriptionStatus = (): ShellSubscriptionStatus =>
  useAuthStore((state) => state.user?.subscriptionStatus ?? 'none');

export const useEntitledLanguagePairs = (): ShellLanguagePairEntitlement[] =>
  useAuthStore((state) => state.user?.entitledLanguagePairs ?? []);

export const useDayPassCreditCount = (): number =>
  useAuthStore((state) => state.user?.dayPassCreditCount ?? 0);

export const useHasLanguagePairEntitlement = (native: string, target: string): boolean =>
  useAuthStore((state) =>
    (state.user?.entitledLanguagePairs ?? []).some(
      (pair) => pair.native === native && pair.target === target
    )
  );
